import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

// Format date to DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const TrainingEntry = () => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingTraining, setViewingTraining] = useState(null);
  const [entries, setEntries] = useState([
    {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      traineeName: '',
      trainingType: '',
      gstRate: 18,
      trainingCost: '',
      trainerFee: '',
      materialCost: '',
      venueCost: '',
      otherExpenses: '',
      gstAmount: '',
      totalCost: '',
      description: '',
      paymentMode: 'cash',
      paymentProof: '',
      utrNo: ''
    }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(0);
  const [viewingImage, setViewingImage] = useState(null);
  const [trainingTypes, setTrainingTypes] = useState([]);

  // Compress image before upload to approximately 50KB
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const TARGET_SIZE_KB = 50;
          const TARGET_SIZE_BYTES = TARGET_SIZE_KB * 1024;
          let MAX_WIDTH = 800;
          let MAX_HEIGHT = 800;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const compressWithQuality = (quality) => {
            return new Promise((resolveBlob) => {
              canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                  resolveBlob({
                    dataUrl: reader.result,
                    size: blob.size,
                    mimeType: 'image/jpeg',
                    quality
                  });
                };
              }, 'image/jpeg', quality);
            });
          };

          const tryCompress = async () => {
            let quality = 0.7;
            let result = await compressWithQuality(quality);
            if (result.size > TARGET_SIZE_BYTES) {
              quality = 0.5;
              result = await compressWithQuality(quality);
            }
            if (result.size > TARGET_SIZE_BYTES) {
              quality = 0.3;
              result = await compressWithQuality(quality);
            }
            if (result.size > TARGET_SIZE_BYTES) {
              quality = 0.2;
              result = await compressWithQuality(quality);
            }
            resolve(result.dataUrl);
          };

          tryCompress();
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    fetchTrainings();
    fetchTrainingTypes();
  }, []);

  useEffect(() => {
    // Auto-calculate total cost, GST and total amount
    setEntries(prevEntries =>
      prevEntries.map(entry => {
        const trainingCost = parseFloat(entry.trainingCost) || 0;
        const trainerFee = parseFloat(entry.trainerFee) || 0;
        const materialCost = parseFloat(entry.materialCost) || 0;
        const venueCost = parseFloat(entry.venueCost) || 0;
        const otherExpenses = parseFloat(entry.otherExpenses) || 0;
        const gstRate = parseFloat(entry.gstRate) || 0;
        const totalCost = trainingCost + trainerFee + materialCost + venueCost + otherExpenses;
        const gstAmount = (totalCost * gstRate) / 100;
        return {
          ...entry,
          totalCost: totalCost ? totalCost.toFixed(2) : '',
          gstAmount: gstAmount ? gstAmount.toFixed(2) : ''
        };
      })
    );
  }, [entries.map(e => e.trainingCost + e.trainerFee + e.materialCost + e.venueCost + e.otherExpenses + e.gstRate).join(',')]);

  const fetchTrainings = async () => {
    try {
      const db = getDatabase();
      const trainingsRef = ref(db, 'HTAMS/expenses/trainingentry');
      const snapshot = await get(trainingsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const trainingsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setTrainings(trainingsList.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } else {
        setTrainings([]);
      }
    } catch (error) {
      console.error('Error fetching trainings:', error);
      toast.error('Failed to fetch training entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingTypes = async () => {
    try {
      const db = getDatabase();
      const trainingTypesRef = ref(db, 'HTAMS/company/trainings');
      const snapshot = await get(trainingTypesRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const trainingsList = [];
        // Extract trainings with location and date
        Object.values(data).forEach(training => {
          if (training.location) {
            trainingsList.push({
              location: training.location,
              startDate: training.startDate,
              displayText: `${training.location} - ${training.startDate ? new Date(training.startDate).toLocaleDateString() : 'TBD'}`
            });
          }
        });
        // Sort by location
        trainingsList.sort((a, b) => a.location.localeCompare(b.location));
        setTrainingTypes(trainingsList);
      } else {
        setTrainingTypes([]);
      }
    } catch (error) {
      console.error('Error fetching training types:', error);
      // Dont show error toast as this is non-critical
    }
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      toast.info('Compressing and uploading image...');
      const compressedDataUrl = await compressImage(file);

      // Convert data URL to blob
      const response = await fetch(compressedDataUrl);
      const blob = await response.blob();

      // Get trainee name for filename (fallback if empty)
      const traineeName = entries[index]?.traineeName || 'unknown';
      const timestamp = Date.now();
      const fileName = `training-proofs/${timestamp}_${traineeName.replace(/[^a-zA-Z0-9]/g, '_')}_${index}.jpg`;

      const storage = getStorage();
      const storageReference = storageRef(storage, fileName);
      await uploadBytes(storageReference, blob);
      const downloadURL = await getDownloadURL(storageReference);

      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, paymentProof: downloadURL } : entry
      ));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    if (['trainingCost', 'trainerFee', 'materialCost', 'venueCost', 'otherExpenses', 'gstRate'].includes(name)) {
      const numericValue = value.replace(/,/g, '');
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : numericValue;
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: validValue } : entry
      ));
      return;
    }

    if (name === 'utrNo') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) =>
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }

    setEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [name]: value } : entry
    ));
  };

  const addNewEntry = () => {
    const newIndex = entries.length;
    setEntries(prev => [...prev, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      traineeName: '',
      trainingType: '',
      gstRate: 18,
      trainingCost: '',
      trainerFee: '',
      materialCost: '',
      venueCost: '',
      otherExpenses: '',
      gstAmount: '',
      totalCost: '',
      description: '',
      paymentMode: 'cash',
      paymentProof: '',
      utrNo: ''
    }]);
    setExpandedEntry(newIndex);
  };

  const removeEntry = (index) => {
    if (entries.length === 1) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      setExpandedEntry(index === 0 ? index - 1 : 0);
      return;
    }
    setEntries(prev => prev.filter((_, i) => i !== index));
    setExpandedEntry(index === 0 ? index - 1 : 0);
    toast.warning('At least one entry is required');
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? null : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    if (!entry.date) {
      toast.error(`Entry ${entryNum}: Date is required`);
      return false;
    }
    if (!entry.traineeName || !entry.traineeName.trim()) {
      toast.error(`Entry ${entryNum}: Trainee name is required`);
      return false;
    }
    if (!entry.trainingType || !entry.trainingType.trim()) {
      toast.error(`Entry ${entryNum}: Training type is required`);
      return false;
    }
    const totalCost = parseFloat(entry.totalCost) || 0;
    if (totalCost === 0) {
      toast.error(`Entry ${entryNum}: At least one cost field must have a value`);
      return false;
    }
    if (!entry.paymentMode) {
      toast.error(`Entry ${entryNum}: Payment mode is required`);
      return false;
    }
    if ((entry.paymentMode === 'cash' || entry.paymentMode === 'online') && !entry.paymentProof) {
      toast.error(`Entry ${entryNum}: Payment proof image is required for ${entry.paymentMode} payment`);
      return false;
    }
    if (entry.paymentMode === 'cheque' && (!entry.utrNo || !entry.utrNo.trim())) {
      toast.error(`Entry ${entryNum}: UTR No / Cheque No is required for cheque payment`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        setExpandedEntry(i);
        return;
      }
    }

    setSubmitting(true);
    try {
      const db = getDatabase();

      if (editingId) {
        const entry = entries[0];
        const trainingData = {
          date: entry.date,
          traineeName: entry.traineeName,
          trainingType: entry.trainingType,
          gstRate: entry.gstRate,
          trainingCost: parseFloat(entry.trainingCost) || 0,
          trainerFee: parseFloat(entry.trainerFee) || 0,
          materialCost: parseFloat(entry.materialCost) || 0,
          venueCost: parseFloat(entry.venueCost) || 0,
          otherExpenses: parseFloat(entry.otherExpenses) || 0,
          gstAmount: parseFloat(entry.gstAmount) || 0,
          totalCost: parseFloat(entry.totalCost) || 0,
          description: entry.description,
          paymentMode: entry.paymentMode,
          paymentProof: entry.paymentProof,
          utrNo: entry.utrNo,
          updatedAt: new Date().toISOString(),
          timestamp: Date.now()
        };
        const trainingRef = ref(db, `HTAMS/expenses/trainingentry/${editingId}`);
        await update(trainingRef, trainingData);
        toast.success('Training entry updated successfully!');
        setEditingId(null);
      } else {
        let successCount = 0;
        for (const entry of entries) {
          const trainingData = {
            date: entry.date,
            traineeName: entry.traineeName,
            trainingType: entry.trainingType,
            gstRate: entry.gstRate,
            trainingCost: parseFloat(entry.trainingCost) || 0,
            trainerFee: parseFloat(entry.trainerFee) || 0,
            materialCost: parseFloat(entry.materialCost) || 0,
            venueCost: parseFloat(entry.venueCost) || 0,
            otherExpenses: parseFloat(entry.otherExpenses) || 0,
            gstAmount: parseFloat(entry.gstAmount) || 0,
            totalCost: parseFloat(entry.totalCost) || 0,
            description: entry.description,
            paymentMode: entry.paymentMode,
            paymentProof: entry.paymentProof,
            utrNo: entry.utrNo,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            timestamp: Date.now()
          };
          const trainingsRef = ref(db, 'HTAMS/expenses/trainingentry');
          const newTrainingRef = push(trainingsRef);
          await set(newTrainingRef, trainingData);
          successCount++;
        }
        toast.success(`${successCount} training ${successCount === 1 ? 'entry' : 'entries'} added successfully!`);
        setShowForm(false);
        setEntries([{
          id: Date.now(),
          date: new Date().toISOString().split('T')[0],
          traineeName: '',
          trainingType: '',
          gstRate: 18,
          trainingCost: '',
          trainerFee: '',
          materialCost: '',
          venueCost: '',
          otherExpenses: '',
          gstAmount: '',
          totalCost: '',
          description: '',
          paymentMode: 'cash',
          paymentProof: '',
          utrNo: ''
        }]);
        fetchTrainings();
      }
    } catch (error) {
      console.error('Error saving trainings:', error);
      toast.error('Failed to save training entries');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (training) => {
    setEditingId(training.id);
    setEntries([{
      id: training.id,
      date: training.date,
      traineeName: training.traineeName || '',
      trainingType: training.trainingType || '',
      gstRate: training.gstRate?.toString() || '18',
      trainingCost: training.trainingCost?.toString() || '',
      trainerFee: training.trainerFee?.toString() || '',
      materialCost: training.materialCost?.toString() || '',
      venueCost: training.venueCost?.toString() || '',
      otherExpenses: training.otherExpenses?.toString() || '',
      gstAmount: training.gstAmount?.toString() || '',
      totalCost: training.totalCost?.toString() || '',
      description: training.description || '',
      paymentMode: training.paymentMode || 'cash',
      paymentProof: training.paymentProof || '',
      utrNo: training.utrNo || ''
    }]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, traineeName) => {
    if (window.confirm(`Are you sure you want to delete training entry for ${traineeName}?`)) {
      try {
        const db = getDatabase();
        const trainingRef = ref(db, `HTAMS/expenses/trainingentry/${id}`);
        await remove(trainingRef);
        toast.success('Training entry deleted successfully!');
        fetchTrainings();
      } catch (error) {
        console.error('Error deleting training:', error);
        toast.error('Failed to delete training entry');
      }
    }
  };

  const handleView = (training) => {
    setViewingTraining(training);
  };

  const closeViewModal = () => {
    setViewingTraining(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowForm(false);
    setEntries([{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      traineeName: '',
      trainingType: '',
      gstRate: 18,
      trainingCost: '',
      trainerFee: '',
      materialCost: '',
      venueCost: '',
      otherExpenses: '',
      gstAmount: '',
      totalCost: '',
      description: '',
      paymentMode: 'cash',
      paymentProof: '',
      utrNo: ''
    }]);
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch =
      training.traineeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      training.trainingType?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate =
      !dateFilter.start || new Date(training.date) >= new Date(dateFilter.start);
    const matchesDateEnd =
      !dateFilter.end || new Date(training.date) <= new Date(dateFilter.end);
    const matchesPaymentMethod =
      !paymentMethodFilter || training.paymentMode === paymentMethodFilter;
    return matchesSearch && matchesDate && matchesDateEnd && matchesPaymentMethod;
  });

  const totalPages = Math.ceil(filteredTrainings.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTrainings = filteredTrainings.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, paymentMethodFilter]);

  const exportToExcel = () => {
    if (filteredTrainings.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const exportData = filteredTrainings.map((training, index) => ({
      'Sr No': index + 1,
      'Date': formatDate(training.date),
      'Trainee Name': training.traineeName || '-',
      'Training Type': training.trainingType || '-',
      'Training Cost': (training.trainingCost || 0).toLocaleString(),
      'Trainer Fee': (training.trainerFee || 0).toLocaleString(),
      'Material Cost': (training.materialCost || 0).toLocaleString(),
      'Venue Cost': (training.venueCost || 0).toLocaleString(),
      'Other Expenses': (training.otherExpenses || 0).toLocaleString(),
      'GST Rate': training.gstRate || 18,
      'GST Amount': (training.gstAmount || 0).toLocaleString(),
      'Total Cost': (training.totalCost || 0).toLocaleString(),
      'Payment Mode': (training.paymentMode || 'cash').toUpperCase(),
      'UTR/Cheque No': training.utrNo || '-',
      'Description': training.description || '-'
    }));

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row =>
        headers.map(header => {
          const value = row[header];
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `TrainingEntries_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredTrainings.length} entries successfully!`);
  };

  if (loading) {
    return <div className="loading">Loading training entries...</div>;
  }

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="page-header">
        <h2>Training Expenses</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="add-btn" onClick={exportToExcel} style={{ background: '#28a745' }}>
            Export to Excel
          </button>
          <button
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? 'Close Add Expenses' : 'Add Expenses'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">
            {editingId ? 'Edit Training Expenses' : 'Add New Training Expenses'}
          </h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => {
              const isExpanded = expandedEntry === index;
              return (
                <div key={entry.id} style={{
                  marginBottom: '10px',
                  border: isExpanded ? '2px solid #2c5f2d' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}>
                  <div
                    onClick={() => toggleEntry(index)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 15px',
                      background: isExpanded ? '#2c5f2d' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <h4 style={{ margin: 0, color: isExpanded ? 'white' : '#2c5f2d', fontWeight: 'bold' }}>
                        Entry {index + 1}
                      </h4>
                      <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666' }}>
                        {entry.traineeName || 'Unnamed trainee'}
                      </span>
                      <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666', fontWeight: 'bold' }}>
                        ₹{entry.totalCost || '0.00'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!editingId && entries.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEntry(index);
                          }}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 15px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Remove
                        </button>
                      )}
                      <span style={{
                        color: isExpanded ? 'white' : '#2c5f2d',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '15px' }}>
                      <div className="form-grid" style={{ gap: '12px' }}>
                        <div className="form-group">
                          <label>Date <span className="required">*</span></label>
                          <input
                            type="date"
                            name="date"
                            value={entry.date}
                            onChange={(e) => handleInputChange(e, index)}
                            required
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Trainee Name <span className="required">*</span></label>
                          <input
                            type="text"
                            name="traineeName"
                            value={entry.traineeName}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Trainee Name"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Training Type <span className="required">*</span></label>
                          <select
                            name="trainingType"
                            value={entry.trainingType}
                            onChange={(e) => handleInputChange(e, index)}
                            className="form-control"
                          >
                            <option value="">Select Training Type</option>
                            {trainingTypes.map((training, i) => (
                              <option key={i} value={training.location}>
                                {training.displayText}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>GST %</label>
                          <input
                            type="number"
                            name="gstRate"
                            value={entry.gstRate}
                            onChange={(e) => handleInputChange(e,
                              index)}
                            placeholder="GST %"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Training Cost</label>
                          <input
                            type="number"
                            name="trainingCost"
                            value={entry.trainingCost}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Training Cost"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Trainer Fee</label>
                          <input
                            type="number"
                            name="trainerFee"
                            value={entry.trainerFee}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Trainer Fee"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Material Cost</label>
                          <input
                            type="number"
                            name="materialCost"
                            value={entry.materialCost}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Material Cost"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Venue Cost</label>
                          <input
                            type="number"
                            name="venueCost"
                            value={entry.venueCost}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Venue Cost"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Other Expenses</label>
                          <input
                            type="number"
                            name="otherExpenses"
                            value={entry.otherExpenses}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Other Expenses"
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>GST Amount</label>
                          <input
                            type="text"
                            value={entry.gstAmount ? entry.gstAmount : ''}
                            readOnly
                            placeholder="GST Amount"
                            className="form-control readonly"
                          />
                        </div>
                        <div className="form-group">
                          <label>Total Cost</label>
                          <input
                            type="text"
                            value={entry.totalCost ? entry.totalCost : ''}
                            readOnly
                            placeholder="Total Cost"
                            className="form-control readonly total-highlight"
                          />
                        </div>
                        <div className="form-group">
                          <label>Description</label>
                          <textarea
                            name="description"
                            value={entry.description}
                            onChange={(e) => handleInputChange(e, index)}
                            placeholder="Enter description or notes"
                            rows={3}
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label>Payment Mode <span className="required">*</span></label>
                          <select
                            name="paymentMode"
                            value={entry.paymentMode}
                            onChange={(e) => handleInputChange(e, index)}
                            className="form-control"
                          >
                            <option value="cash">Cash</option>
                            <option value="online">Online</option>
                            <option value="cheque">Cheque</option>
                          </select>
                        </div>

                        {entry.paymentMode !== 'cash' && (
                          <div className="form-group">
                            <label>Upload Image <span className="required">*</span></label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, index)}
                              className="form-control"
                            />
                            {entry.paymentProof && (
                              <div className="image-preview" style={{
                                marginTop: '10px',
                                position: 'relative',
                                display: 'inline-block'
                              }}>
                                <img
                                  src={entry.paymentProof}
                                  alt="Payment Proof"
                                  style={{
                                    maxWidth: '200px',
                                    maxHeight: '200px',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '5px'
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setEntries(prev => prev.map((e, i) =>
                                    i === index ? { ...e, paymentProof: '' } : e
                                  ))}
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    background: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '30px',
                                    height: '30px',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    lineHeight: 1,
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                  }}
                                  title="Remove image"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {entry.paymentMode === 'cheque' && (
                          <div className="form-group">
                            <label>UTR No / Cheque No</label>
                            <input
                              type="text"
                              name="utrNo"
                              value={entry.utrNo}
                              onChange={(e) => handleInputChange(e, index)}
                              placeholder="Enter UTR or Cheque number"
                              className="form-control"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!editingId && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (
                  editingId ? 'Update Training' : `Save ${entries.length} Training${entries.length > 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by trainee or training type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: 2 }}
        />
        <select
          value={paymentMethodFilter}
          onChange={(e) => setPaymentMethodFilter(e.target.value)}
          className="search-input"
          style={{ cursor: 'pointer' }}
        >
          <option value="">All Payment Methods</option>
          <option value="cash">Cash</option>
          <option value="online">Online</option>
          <option value="cheque">Cheque</option>
        </select>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({
              ...dateFilter,
              start: e.target.value
            })}
            className="date-input"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({
              ...dateFilter,
              end: e.target.value
            })}
            className="date-input"
          />
        </div>
        {(searchTerm || paymentMethodFilter || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setPaymentMethodFilter('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Trainee Name</th>
              <th>Training Type</th>
              <th>Training Cost</th>
              <th>Trainer Fee</th>
              <th>Total Cost</th>
              <th>Payment Mode</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTrainings.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No training entries found</h3>
                    <p>Add training entries using the "Add Expenses" button above.</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentTrainings.map((training, index) => (
                <tr key={training.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(training.date)}</td>
                  <td>{training.traineeName || '-'}</td>
                  <td>{training.trainingType || '-'}</td>
                  <td>₹{(training.trainingCost || 0).toLocaleString()}</td>
                  <td>₹{(training.trainerFee || 0).toLocaleString()}</td>
                  <td>₹{(training.totalCost || 0).toLocaleString()}</td>
                  <td>
                    <span className={`payment-badge payment-${training.paymentMode}`}>
                      {training.paymentMode === 'cash' ? 'CASH' : training.paymentMode.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleView(training)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(training)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(training.id, training.traineeName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredTrainings.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTrainings.length)} of {filteredTrainings.length} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              if (pageNumber === 1 || pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                return (
                  <span key={pageNumber} className="pagination-dots">...</span>
                );
              }
              return null;
            })}
            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingTraining && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Training Expenses Details</h3>
              <button className="modal-close" onClick={closeViewModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date</label>
                  <span>{formatDate(viewingTraining.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Trainee Name</label>
                  <span>{viewingTraining.traineeName || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Training Type</label>
                  <span>{viewingTraining.trainingType || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>GST Rate</label>
                  <span>{viewingTraining.gstRate || 18}%</span>
                </div>
                <div className="detail-item">
                  <label>Training Cost</label>
                  <span>₹{(viewingTraining.trainingCost || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Trainer Fee</label>
                  <span>₹{(viewingTraining.trainerFee || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Material Cost</label>
                  <span>₹{(viewingTraining.materialCost || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Venue Cost</label>
                  <span>₹{(viewingTraining.venueCost || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Other Expenses</label>
                  <span>₹{(viewingTraining.otherExpenses || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>GST Amount</label>
                  <span>₹{(viewingTraining.gstAmount || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Total Cost</label>
                  <span className="highlight-amount">₹{(viewingTraining.totalCost || 0).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Payment Mode</label>
                  <span className={`payment-badge payment-${viewingTraining.paymentMode}`}>
                    {viewingTraining.paymentMode === 'cash' ? 'CASH' : viewingTraining.paymentMode.toUpperCase()}
                  </span>
                </div>

                {viewingTraining.paymentMode !== 'cash' && viewingTraining.paymentProof && (
                  <div className="detail-item full-width">
                    <label>Payment Proof</label>
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={viewingTraining.paymentProof}
                        alt="Payment Proof"
                        style={{
                          maxWidth: '300px',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '5px',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingImage(viewingTraining.paymentProof);
                        }}
                      />
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingImage(viewingTraining.paymentProof);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          View Full Image
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement('a');
                            link.href = viewingTraining.paymentProof;
                            link.download = `payment-proof-${viewingTraining.traineeName}-image.jpg`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Download Image
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {viewingTraining.paymentMode === 'cheque' && viewingTraining.utrNo && (
                  <div className="detail-item">
                    <label>UTR No / Cheque No</label>
                    <span>{viewingTraining.utrNo}</span>
                  </div>
                )}

                {viewingTraining.description && (
                  <div className="detail-item full-width">
                    <label>Description</label>
                    <span>{viewingTraining.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewing Modal */}
      {viewingImage && (
        <div className="modal-overlay" onClick={() => setViewingImage(null)} style={{ zIndex: 1001 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            padding: '20px',
            overflow: 'auto'
          }}>
            <div className="modal-header">
              <h3>Payment Proof Image</h3>
              <button className="modal-close" onClick={() => setViewingImage(null)}>×</button>
            </div>
            <div style={{ padding: '20px', textAlign: 'center', overflow: 'auto' }}>
              <img
                src={viewingImage}
                alt="Payment Proof"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
              <div style={{ marginTop: '20px' }}>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = viewingImage;
                    link.download = 'payment-proof.jpg';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  Download Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingEntry;
